// Lightweight reverse-geocoding helper. Uses Google Maps Geocoder when available,
// falls back to OpenStreetMap Nominatim (no key required, slower & rate-limited).
// Results are cached in-memory + sessionStorage for the lifetime of the tab.

const memCache = new Map<string, string>();

function key(lat: number, lng: number) {
  return `${lat.toFixed(5)},${lng.toFixed(5)}`;
}

function readSessionCache(k: string): string | null {
  try {
    return sessionStorage.getItem(`geo:${k}`);
  } catch {
    return null;
  }
}
function writeSessionCache(k: string, v: string) {
  try {
    sessionStorage.setItem(`geo:${k}`, v);
  } catch {
    /* ignore */
  }
}

async function viaGoogle(lat: number, lng: number): Promise<string | null> {
  // @ts-ignore — google maps may or may not be loaded
  const g = (window as any).google;
  if (!g?.maps?.Geocoder) return null;
  return new Promise((resolve) => {
    try {
      const geocoder = new g.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results: any[], status: string) => {
        if (status === "OK" && results?.[0]?.formatted_address) {
          resolve(results[0].formatted_address);
        } else {
          resolve(null);
        }
      });
    } catch {
      resolve(null);
    }
  });
}

async function viaNominatim(lat: number, lng: number): Promise<string | null> {
  try {
    const r = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
      { headers: { Accept: "application/json" } }
    );
    if (!r.ok) return null;
    const j = await r.json();
    return j?.display_name || null;
  } catch {
    return null;
  }
}

export async function reverseGeocode(lat: number, lng: number): Promise<string> {
  const k = key(lat, lng);
  if (memCache.has(k)) return memCache.get(k)!;
  const cached = readSessionCache(k);
  if (cached) {
    memCache.set(k, cached);
    return cached;
  }
  const result =
    (await viaGoogle(lat, lng)) ||
    (await viaNominatim(lat, lng)) ||
    `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  memCache.set(k, result);
  writeSessionCache(k, result);
  return result;
}
